import React from 'react';
import { FOCUS_RING } from './focusRing';

interface CheckboxProps {
    checked: boolean;
    /** Tri-state "partial" look — overrides the checked tick with a square. */
    indeterminate?: boolean;
    /** Optional when `presentational` — a presentational checkbox is a visual indicator only. */
    onChange?: (checked: boolean) => void;
    disabled?: boolean;
    size?: number;
    /**
     * Render a visual-only indicator (a non-interactive, non-focusable `<span>`,
     * `aria-hidden`) instead of an interactive checkbox. Use this inside a clickable
     * row/card whose wrapper is the real control, so there's a single tab stop.
     */
    presentational?: boolean;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    className?: string;
    id?: string;
}

const Checkbox: React.FC<CheckboxProps> = ({
    checked,
    indeterminate = false,
    onChange,
    disabled = false,
    size = 14,
    presentational = false,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    className = '',
    id,
}) => {
    const isFilled = checked && !indeterminate;
    const radius = Math.max(3, Math.round(size * 0.36));
    const innerSize = Math.round(size * 0.57);

    // Indeterminate shares the checked border rather than a fainter one, so "some selected"
    // never reads weaker than "nothing selected"; the transparent fill separates the states.
    const borderColor =
        indeterminate || checked
            ? 'var(--malachite-accent-background, rgb(0, 175, 102))'
            : 'var(--malachite-text-placeholder, rgb(187, 187, 187))';

    const boxStyle: React.CSSProperties = {
        position: 'relative',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${size}px`,
        height: `${size}px`,
        padding: 0,
        borderRadius: `${radius}px`,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor,
        backgroundColor: isFilled
            ? 'var(--malachite-accent-background, rgb(0, 175, 102))'
            : 'transparent',
        color: '#fff',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color 150ms ease, border-color 150ms ease',
    };

    const glyph = (
        <>
            {isFilled && (
                <svg
                    width={Math.round(size * 0.78)}
                    height={Math.round(size * 0.78)}
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                >
                    <title>checked</title>
                    {/* Filled glyph — the library forces `svg path { fill: currentColor }` */}
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
            )}
            {indeterminate && (
                <span
                    style={{
                        width: `${innerSize}px`,
                        height: `${innerSize}px`,
                        borderRadius: `${Math.max(2, Math.round(radius * 0.6))}px`,
                        backgroundColor: 'var(--malachite-accent-background, rgb(0, 175, 102))',
                    }}
                />
            )}
        </>
    );

    if (presentational) {
        return (
            <span aria-hidden="true" className={className} style={boxStyle}>
                {glyph}
            </span>
        );
    }

    return (
        <button
            type="button"
            role="checkbox"
            id={id}
            aria-checked={indeterminate ? 'mixed' : checked}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            disabled={disabled}
            onClick={() => onChange?.(indeterminate ? true : !checked)}
            className={`${FOCUS_RING} ${className}`}
            style={{ ...boxStyle, cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
            {glyph}
        </button>
    );
};

export default Checkbox;
export { Checkbox };
export type { CheckboxProps };
